import { OcrResult, OcrProgress, ImageDataLike } from './ocr-types'
import { OcrDetector } from './ocr-detector'
import { OcrDbDetector } from './ocr-db-detector'
import type { TextLineRegion } from './ocr-db-detector'
import { OcrRecognizer } from './ocr-recognizer'
import { downloadOcrConfig, downloadIdToToken, downloadModelBuffer } from './ocr-model-loader'

interface ChunkResult {
  text: string
  confidence: number
}

interface RecognizeResult {
  text: string
  confidence: number
}

export interface OcrEngineOptions {
  modelBaseUrl: string
  onProgress?: (progress: OcrProgress) => void
}

export class OcrEngine {
  private detector: OcrDetector
  private dbDetector: OcrDbDetector | null = null
  private recognizer: OcrRecognizer | null = null
  private options: OcrEngineOptions
  private initialized = false

  constructor(options: OcrEngineOptions) {
    this.options = options
    this.detector = new OcrDetector()
  }

  get isInitialized(): boolean {
    return this.initialized
  }

  get hasDbDetector(): boolean {
    return this.dbDetector?.isInitialized ?? false
  }

  async initialize(): Promise<void> {
    if (this.initialized) return

    this.options.onProgress?.({
      stage: 'loading-models',
      totalLines: 0,
      processedLines: 0,
    })

    const config = await downloadOcrConfig(this.options.modelBaseUrl)
    const idToToken = await downloadIdToToken(this.options.modelBaseUrl)

    this.recognizer = new OcrRecognizer(config, this.options.modelBaseUrl)
    await this.recognizer.initialize(idToToken)

    try {
      const dbModelBuffer = await downloadModelBuffer(this.options.modelBaseUrl, 'detector.onnx')
      this.dbDetector = new OcrDbDetector()
      await this.dbDetector.initialize(dbModelBuffer)
      console.log('[OCR Engine] DB detector loaded successfully')
    } catch (err) {
      this.dbDetector = null
      console.warn('[OCR Engine] DB detector not available, using projection fallback:', err instanceof Error ? err.message : err)
    }

    this.initialized = true
  }

  async extractText(pageImage: ImageDataLike): Promise<OcrResult[]> {
    if (!this.recognizer) {
      throw new Error('OCR engine not initialized. Call initialize() first.')
    }

    this.options.onProgress?.({
      stage: 'detecting',
      totalLines: 0,
      processedLines: 0,
    })

    let lines: TextLineRegion[]

    if (this.dbDetector?.isInitialized) {
      const t0 = performance.now()
      lines = await this.dbDetector.detectLinesAsync(pageImage)
      console.log(`[OCR Engine] DB detector found ${lines.length} lines in ${(performance.now() - t0).toFixed(0)}ms`)
    } else {
      const t0 = performance.now()
      lines = this.detector.detectLines(pageImage)
      console.log(`[OCR Engine] Projection detector found ${lines.length} lines in ${(performance.now() - t0).toFixed(0)}ms`)
    }

    if (lines.length === 0) {
      this.options.onProgress?.({
        stage: 'complete',
        totalLines: 0,
        processedLines: 0,
      })
      return []
    }

    const results: OcrResult[] = []

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i]
      if (!line) continue

      this.options.onProgress?.({
        stage: 'recognizing',
        totalLines: lines.length,
        processedLines: i,
      })

      try {
        const lineImageData = cropImageData(pageImage, line)

        if ((window as any).__OCR_DEBUG__) {
          const d = lineImageData.data
          let pmin = 255, pmax = 0, psum = 0
          for (let i = 0; i < d.length; i += 4) {
            const v = (d[i]! + d[i+1]! + d[i+2]!) / 3
            if (v < pmin) pmin = v
            if (v > pmax) pmax = v
            psum += v
          }
          const pmean = psum / (d.length / 4)
          const isBlank = pmax - pmin < 5
          console.log(`[OCR Crop] line ${line.lineNumber} — ${lineImageData.width}x${lineImageData.height} — min:${pmin.toFixed(0)} max:${pmax.toFixed(0)} mean:${pmean.toFixed(1)} ${isBlank ? '⚠️ NEARLY BLANK' : ''}`)
        }

        const result = await this.recognizeLineWithChunking(lineImageData)

        if (result.text.trim()) {
          results.push({
            text: result.text.trim(),
            confidence: result.confidence,
            box: {
              x: line.x,
              y: line.y,
              width: line.width,
              height: line.height,
            },
            lineNumber: line.lineNumber,
          })

          this.options.onProgress?.({
            stage: 'recognizing',
            totalLines: lines.length,
            processedLines: i + 1,
            currentLineText: result.text.trim(),
          })
        }
      } catch (err) {
        console.warn(`OCR failed for line ${line.lineNumber}:`, err)
      }
    }

    this.options.onProgress?.({
      stage: 'complete',
      totalLines: lines.length,
      processedLines: results.length,
    })

    if ((window as any).__OCR_DEBUG__) {
      console.groupCollapsed(`[OCR Engine] ${results.length}/${lines.length} lines recognized`)
      console.log('Page image:', pageImage.width, 'x', pageImage.height)
      results.forEach((r) => {
        console.log(`  L${r.lineNumber}: "${r.text}" (conf: ${r.confidence.toFixed(3)}, box: ${r.box.x},${r.box.y} ${r.box.width}x${r.box.height})`)
      })
      console.groupEnd()
    }

    return results
  }

  private async recognizeLineWithChunking(lineImage: ImageDataLike): Promise<RecognizeResult> {
    if (!this.recognizer) return { text: '', confidence: 0 }

    const config = this.recognizer.config
    const imgH = config.imgH
    const imgW = config.imgW
    const { width: origW, height: origH } = lineImage

    const maxOrigWidth = ((imgW - 4) * origH) / imgH

    if (origW <= maxOrigWidth) {
      const result = await this.recognizer.recognize(lineImage)
      return { text: result.text, confidence: result.confidence }
    }

    const overlap = maxOrigWidth * 0.15
    const stride = maxOrigWidth - overlap
    const numChunks = Math.ceil((origW - overlap) / stride)
    const chunks: ChunkResult[] = []

    for (let c = 0; c < numChunks; c++) {
      const x0 = Math.floor(c * stride)
      const x1 = Math.min(origW, Math.ceil(x0 + maxOrigWidth))
      const chunk = cropImageData(lineImage, {
        x: x0,
        y: 0,
        width: x1 - x0,
        height: origH,
      })

      try {
        const result = await this.recognizer.recognize(chunk)
        chunks.push({ text: result.text, confidence: result.confidence })
      } catch (err) {
        console.warn(`OCR chunk ${c} failed:`, err)
      }
    }

    const text = mergeChunkTexts(chunks)
    const avgConfidence = chunks.length > 0
      ? chunks.reduce((sum, c) => sum + c.confidence, 0) / chunks.length
      : 0
    return { text, confidence: avgConfidence }
  }

  dispose(): void {
    if (this.recognizer) {
      this.recognizer.dispose()
    }
    if (this.dbDetector) {
      this.dbDetector.dispose()
    }
    this.recognizer = null
    this.dbDetector = null
    this.initialized = false
  }
}

function cropImageData(
  source: ImageDataLike,
  region: { x: number; y: number; width: number; height: number }
): ImageDataLike {
  const x = Math.max(0, Math.floor(region.x))
  const y = Math.max(0, Math.floor(region.y))
  const w = Math.max(1, Math.min(source.width - x, Math.ceil(region.width)))
  const h = Math.max(1, Math.min(source.height - y, Math.ceil(region.height)))

  const cropped = new Uint8ClampedArray(w * h * 4)

  for (let row = 0; row < h; row++) {
    const srcOffset = ((y + row) * source.width + x) * 4
    const dstOffset = row * w * 4
    cropped.set(
      source.data.subarray(srcOffset, srcOffset + w * 4),
      dstOffset
    )
  }

  return { data: cropped, width: w, height: h }
}

function mergeChunkTexts(chunks: ChunkResult[]): string {
  if (chunks.length === 0) return ''
  if (chunks.length === 1) return chunks[0]!.text

  const texts = chunks.map((c) => c.text)
  let result = texts[0]!

  for (let i = 1; i < texts.length; i++) {
    const prev = result
    const curr = texts[i] ?? ''
    if (!prev || !curr) {
      result += curr
      continue
    }

    const maxK = Math.min(prev.length, curr.length, 15)
    let bestK = 0
    let bestScore = 0

    for (let k = maxK; k >= 2; k--) {
      const prevEnd = prev.slice(-k)
      const currStart = curr.slice(0, k)
      let matches = 0
      for (let j = 0; j < k; j++) {
        if (prevEnd[j] === currStart[j]) matches++
      }
      const score = matches / k
      if (score > bestScore) {
        bestScore = score
        bestK = k
      }
      if (score === 1) break
    }

    if (bestScore >= 0.7) {
      result += curr.slice(bestK)
    } else {
      result += curr
    }
  }

  return result
}
